import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, Github, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTranslatedProjectData } from "../utils/projectTranslations";
import SectionHeader from "./ui/SectionHeader";
import { cn, spacing, animations } from "../utils/cn";
import { useNavigation } from "../contexts/NavigationContext";

const FeaturedProjects = () => {
  const { t } = useTranslation();
  const translatedData = useTranslatedProjectData();
  const featuredProjects = translatedData.slice(0, 3);
  const location = useLocation();
  const { rememberLocation } = useNavigation();

  const handleViewDetailsClick = () => {
    // Remember current location (including hash) and scroll position
    const fullLocation = location.pathname + location.hash;
    rememberLocation(fullLocation, window.scrollY);
  };

  return (
    <section id="work" className={cn("bg-navy", spacing.section)}>
      <div className={spacing.container}>
        <motion.div {...animations.fadeInUp}>
          {/* Custom Section Header */}
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-accent font-mono text-sm sm:text-base mb-3 sm:mb-4">
                <span className="text-accent font-mono mr-2">04.</span>
                {t('work.title')}
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-semibold text-lighterSlate mb-4 sm:mb-6">
                {t('work.subtitle')}
              </h2>
              <div className="w-16 sm:w-20 lg:w-24 h-0.5 bg-gradient-to-r from-accent to-accent/20 mx-auto mb-4 sm:mb-6"></div>
              <p className="text-slate text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-4">
                {t('work.description')}
              </p>
            </motion.div>
          </div>

          <div className="space-y-16 sm:space-y-20 lg:space-y-24">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="relative group"
                data-project="featured"
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="grid grid-cols-12 gap-6 lg:gap-4 items-center">
                  {/* Project Image */}
                  <div className={`col-span-12 lg:col-span-7 ${
                    index % 2 === 0 ? '' : 'lg:col-start-6'
                  }`}>
                    <div className="relative group/image">
                      <a
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block relative rounded-lg overflow-hidden bg-accent/5 shadow-2xl group-hover:shadow-accent/10 transition-all duration-500"
                      >
                        <div className="aspect-[3/2] relative">
                          <img
                            src={project.img}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-br from-navy/80 via-navy/60 to-accent/20 mix-blend-multiply group-hover:from-navy/40 group-hover:via-navy/20 group-hover:to-accent/10 transition-all duration-500"></div>
                          
                          {/* Subtle border glow */}
                          <div className="absolute inset-0 rounded-lg border-2 border-accent/0 group-hover:border-accent/30 transition-all duration-500"></div>
                          
                          {/* Corner accent */}
                          <div className="absolute top-4 right-4 w-3 h-3 bg-accent rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 delay-200"></div>
                        </div>
                      </a>
                    </div>
                  </div>

                  {/* Project Content */}
                  <div className={`col-span-12 lg:col-span-6 ${
                    index % 2 === 0 
                      ? 'lg:col-start-6 lg:text-right' 
                      : 'lg:col-start-1 lg:text-left'
                  } mt-6 lg:mt-0 lg:-mt-8 relative z-10`}>
                    
                    {/* Project Label */}
                    <motion.p 
                      className={`text-accent font-mono text-sm mb-3 flex items-center ${
                        index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'
                      }`}
                      initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <span className={`w-8 sm:w-12 h-px bg-accent ${
                        index % 2 === 0 ? 'lg:order-2 lg:ml-4 mr-4 lg:mr-0' : 'mr-4'
                      }`}></span>
                      {t('work.featuredProject')}
                    </motion.p>

                    {/* Project Title */}
                    <motion.h3 
                      className="text-xl sm:text-2xl lg:text-3xl font-bold text-lighterSlate mb-4 sm:mb-6 group-hover:text-accent transition-all duration-300"
                      initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                    >
                      <a
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline decoration-accent decoration-2 underline-offset-4"
                      >
                        {project.title}
                      </a>
                    </motion.h3>

                    {/* Project Description */}
                    <motion.div 
                      className="bg-lightNavy/90 backdrop-blur-sm p-4 sm:p-5 lg:p-6 rounded-xl shadow-2xl mb-4 sm:mb-6 relative z-20 border border-accent/10"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                    >
                      <p className="text-slate leading-relaxed text-sm sm:text-base">
                        {project.desc}
                      </p>
                      {/* Subtle corner decoration */}
                      <div className="absolute top-3 right-3 w-1 h-1 bg-accent rounded-full opacity-60"></div>
                    </motion.div>

                    {/* Tech Stack */}
                    {project.techStack && (
                      <motion.ul 
                        className={`flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 font-mono text-xs sm:text-sm ${
                          index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'
                        }`}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                      >
                        {project.techStack.map((tech, techIndex) => (
                          <motion.li 
                            key={tech} 
                            className="text-slate hover:text-accent transition-all duration-300 cursor-default px-2 py-1 rounded-md hover:bg-accent/10"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: 0.6 + techIndex * 0.1 }}
                            whileHover={{ scale: 1.05 }}
                          >
                            {tech}
                          </motion.li>
                        ))}
                      </motion.ul>
                    )}

                    {/* Project Links */}
                    <motion.div 
                      className={`flex items-center gap-4 ${
                        index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'
                      }`}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.7 }}
                    >
                      {/* View Details Link for all featured projects */}
                      <Link
                        to={`/project-detail/${project.id}`}
                        onClick={handleViewDetailsClick}
                        className="inline-flex items-center px-4 py-2 bg-accent/10 text-accent rounded-md text-sm font-medium hover:bg-accent/20 transition-all duration-300 border border-accent/20"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('work.viewDetails')}
                      </Link>
                      
                      {project.githubUrl && (
                        <motion.a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate hover:text-accent transition-all duration-300 p-2 rounded-lg hover:bg-accent/10"
                          aria-label="GitHub Repository"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Github className="w-6 h-6" />
                        </motion.a>
                      )}
                      {project.demoUrl && (
                        <motion.a
                          href={project.demoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate hover:text-accent transition-all duration-300 p-2 rounded-lg hover:bg-accent/10"
                          aria-label="Live Demo"
                          whileHover={{ scale: 1.1, rotate: -5 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <ExternalLink className="w-6 h-6" />
                        </motion.a>
                      )}
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProjects;